/* Floretti 3.0 — prijs per wasbeurt.

   Twee taken:
   1. De staven pas laten uitvouwen zodra de sectie in beeld komt.
   2. De schuifregelaar (wasjes per week) live doorrekenen naar de
      jaarkosten per optie en de besparing ten opzichte van de
      duurste alternatieve vorm.

   Custom element, zodat de sectie zichzelf opnieuw initialiseert in
   de theme-editor. Alle bedragen komen als centen uit Liquid, zodat
   er niets uit opgemaakte tekst geparsed hoeft te worden. */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  var WEEKS_PER_YEAR = 52;

  function formatEuro(cents) {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  }

  class FlorettiPricePerWash extends HTMLElement {
    connectedCallback() {
      this.rows = Array.prototype.slice.call(this.querySelectorAll('[data-cents]'));
      this.slider = this.querySelector('[data-washes-input]');
      this.washesOut = this.querySelector('[data-washes-out]');
      this.amountOut = this.querySelector('[data-savings-amount]');
      this.noteOut = this.querySelector('[data-savings-note]');

      this.unitOut = this.querySelector('[data-unit-out]');
      this.refSelect = this.querySelector('[data-ref-select]');
      this.doseInputs = Array.prototype.slice.call(this.querySelectorAll('[data-dose-input]'));

      this.primaryCents = parseInt(this.getAttribute('data-primary-cents'), 10) || 0;
      this.refCents = parseInt(this.getAttribute('data-ref-cents'), 10) || 0;
      this.yearTemplate = this.getAttribute('data-year-template') || '{bedrag} per jaar';
      this.noteTemplate = this.getAttribute('data-note-template') || '';
      /* Bij één wasje hoort een enkelvoudige eenheid — "1 wasjes" leest fout. */
      this.unitMany = this.getAttribute('data-unit-many') || '';
      this.unitOne = this.getAttribute('data-unit-one') || this.unitMany;

      this.readReference();
      this.reveal();

      this._onInput = this.update.bind(this);

      if (this.slider) this.slider.addEventListener('input', this._onInput);
      if (this.refSelect) this.refSelect.addEventListener('change', this._onInput);
      this.doseInputs.forEach(function (input) {
        input.addEventListener('change', this._onInput);
      }, this);
      if (this.slider) this.update();
    }

    disconnectedCallback() {
      if (this._observer) this._observer.disconnect();
      if (this._onInput) {
        if (this.slider) this.slider.removeEventListener('input', this._onInput);
        if (this.refSelect) this.refSelect.removeEventListener('change', this._onInput);
        this.doseInputs.forEach(function (input) {
          input.removeEventListener('change', this._onInput);
        }, this);
      }
    }

    /* De bezoeker kiest zelf waarmee hij vergelijkt; zonder keuzelijst
       blijft de duurste optie uit Liquid staan. */
    readReference() {
      if (!this.refSelect) {
        this.refLabel = '';
        return;
      }
      var option = this.refSelect.options[this.refSelect.selectedIndex];
      if (!option) return;
      var cents = parseInt(option.value, 10);
      if (!isNaN(cents)) this.refCents = cents;
      this.refLabel = option.textContent.trim();
    }

    /* De staven staan in CSS op width:0 en krijgen hun eindbreedte via
       .is-revealed. Zonder IntersectionObserver zetten we hem direct. */
    reveal() {
      var el = this;
      if (REDUCED.matches || !('IntersectionObserver' in window)) {
        el.classList.add('is-revealed');
        return;
      }

      var observer = new IntersectionObserver(
        function (entries, obs) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            el.classList.add('is-revealed');
            obs.disconnect();
          });
        },
        { threshold: 0.25 }
      );
      observer.observe(el);
      this._observer = observer;
    }

    /* Een wasbeurt op de verpakking is een halve strip. Kiest de bezoeker een
       middelgrote was (1 strip), dan is de Floretti-prijs per was twee keer
       zo hoog. Zonder keuzeveld blijft de waarde uit Liquid staan. */
    doseMultiplier() {
      var checked = this.doseInputs.filter(function (input) { return input.checked; })[0];
      var mult = checked ? parseFloat(checked.value) : NaN;
      return isNaN(mult) ? null : mult;
    }

    /* Prijs, balk en jaarbedrag van de Floretti-rij volgen de gekozen dosering. */
    applyDose() {
      var mult = this.doseMultiplier();
      if (mult === null) return;

      var effective = this.rows.map(function (row) {
        if (row.classList.contains('is-primary')) {
          var exact = parseFloat(row.getAttribute('data-exact-cents'));
          return isNaN(exact) ? parseInt(row.getAttribute('data-cents'), 10) : Math.round(exact * mult);
        }
        return parseInt(row.getAttribute('data-cents'), 10);
      });
      var max = Math.max.apply(null, effective.filter(function (c) { return !isNaN(c); }));

      this.rows.forEach(function (row, i) {
        var cents = effective[i];
        if (isNaN(cents)) return;
        row.setAttribute('data-cents', String(cents));
        var price = row.querySelector('.ppw-row__price b');
        if (price) price.textContent = '\u20AC\u00A0' + (cents / 100).toFixed(2).replace('.', ',');
        var bar = row.querySelector('.ppw-row__bar');
        if (bar && max > 0) bar.style.setProperty('--w', Math.round((cents * 100) / max) + '%');
        if (row.classList.contains('is-primary')) this.primaryCents = cents;
      }, this);
    }

    update() {
      var washes = parseInt(this.slider.value, 10);
      if (isNaN(washes)) return;

      this.applyDose();

      this.readReference();

      var washesPerYear = washes * WEEKS_PER_YEAR;
      var unit = washes === 1 ? this.unitOne : this.unitMany;

      if (this.washesOut) this.washesOut.textContent = String(washes);
      if (this.unitOut) this.unitOut.textContent = unit;

      this.rows.forEach(function (row) {
        var cents = parseInt(row.getAttribute('data-cents'), 10);
        var out = row.querySelector('[data-year-out]');
        if (!out || isNaN(cents)) return;
        out.textContent = this.yearTemplate.replace('{bedrag}', formatEuro(cents * washesPerYear));
      }, this);

      var savedCents = (this.refCents - this.primaryCents) * washesPerYear;
      if (savedCents < 0) savedCents = 0;

      if (this.amountOut) this.amountOut.textContent = formatEuro(savedCents);
      if (this.noteOut && this.noteTemplate) {
        this.noteOut.textContent = this.noteTemplate
          .replace('{wasjes}', String(washes))
          .replace('{eenheid}', unit)
          .replace('{optie}', this.refLabel);
      }
    }
  }

  if (!customElements.get('floretti-price-per-wash')) {
    customElements.define('floretti-price-per-wash', FlorettiPricePerWash);
  }
})();
